import { doc, updateDoc } from "firebase/firestore";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "../contexts/AuthContext";
import { reportOnboardingFailure } from "../lib/onboardingTelemetry";
import { db } from "../services/Firebase";

type Dismissal = "pending" | "saved" | null;
const changeEvent = "wavegen:setup-pill-dismissed";
// Preserve the choice across route remounts even when browser storage is blocked.
const memoryFallback = new Map<string, Dismissal>();
const inFlight = new Map<string, Promise<void>>();

function readDismissal(key: string | null): Dismissal {
  if (!key) return null;
  if (memoryFallback.has(key)) return memoryFallback.get(key)!;
  try {
    const value = window.localStorage.getItem(key);
    return value === "pending" || value === "saved" ? value : null;
  } catch {
    return null;
  }
}

function writeDismissal(key: string, value: Exclude<Dismissal, null>) {
  try {
    window.localStorage.setItem(key, value);
    memoryFallback.delete(key);
  } catch {
    memoryFallback.set(key, value);
  }
  window.dispatchEvent(new Event(changeEvent));
}

function subscribe(onChange: () => void) {
  window.addEventListener(changeEvent, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(changeEvent, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useSetupPillDismissal() {
  const { currentUser, activeWorkspaceId, activeWorkspace, refreshCustomerData } = useAuth();
  const key = currentUser && activeWorkspaceId
    ? `wavegen.setupPillDismissed:${encodeURIComponent(currentUser.uid)}:${encodeURIComponent(activeWorkspaceId)}`
    : null;
  const localDismissal = useSyncExternalStore(
    subscribe,
    () => readDismissal(key),
    () => null,
  );
  const serverDismissed = activeWorkspace?.setupPillDismissed === true;

  const dismiss = useCallback(() => {
    if (key) writeDismissal(key, "pending");
  }, [key]);

  useEffect(() => {
    if (!key || !currentUser || !activeWorkspaceId || localDismissal !== "pending") return;
    if (serverDismissed) {
      writeDismissal(key, "saved");
      return;
    }
    let canceled = false;
    let running = false;
    let reportedFailure = false;
    const sync = async () => {
      if (running || readDismissal(key) !== "pending" || navigator.onLine === false) return;
      running = true;
      try {
        // Navigation can remount the sidebar while Firebase refreshes a token.
        // Reuse that save, retaining the original user and workspace scope.
        let request = inFlight.get(key);
        if (!request) {
          request = (async () => {
            const token = await currentUser.getIdToken();
            const response = await fetch(
              `${process.env.REACT_APP_API_BASE || "https://api.wavegen.ai"}/api/workspaces/${encodeURIComponent(activeWorkspaceId)}/preferences`,
              {
                method: "PATCH",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                  "x-workspace-id": activeWorkspaceId,
                },
                body: JSON.stringify({ setupPillDismissed: true }),
              },
            );
            if (response.status === 404 && activeWorkspaceId === currentUser.uid) {
              // Compatibility with the pre-workspace server is only valid for
              // the original owner's ledger, never another workspace's data.
              await updateDoc(
                doc(db, process.env.REACT_APP_CUSTOMERS_DB ?? "customers_test", activeWorkspaceId),
                { "metadata.setupPillDismissed": true },
              );
            } else if (!response.ok) {
              throw Object.assign(new Error("Could not save workspace preference."), { status: response.status });
            }
          })();
          inFlight.set(key, request);
          void request.finally(() => inFlight.delete(key)).catch(() => {});
        }
        await request;
        writeDismissal(key, "saved");
        if (!canceled) {
          try {
            await refreshCustomerData();
          } catch (error) {
            console.error("Failed to refresh workspace after dismissing setup pill:", error);
          }
        }
      } catch (error) {
        // Dismissal is a durable local preference, even if auth/network fails.
        // Retry on recovery without asking the user to click X again.
        if (!canceled && !reportedFailure) {
          reportedFailure = true;
          console.error("Failed to sync setup pill dismissal; will retry:", error);
          reportOnboardingFailure("dismiss_setup_pill", activeWorkspaceId, (error as { status?: number })?.status, error);
        }
      } finally {
        running = false;
      }
    };
    const retryWhenVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };
    void sync();
    window.addEventListener("online", retryWhenVisible);
    document.addEventListener("visibilitychange", retryWhenVisible);
    // Auth endpoint failures need not trigger the browser's online event.
    const timer = window.setInterval(retryWhenVisible, 60_000);
    return () => {
      canceled = true;
      window.clearInterval(timer);
      window.removeEventListener("online", retryWhenVisible);
      document.removeEventListener("visibilitychange", retryWhenVisible);
    };
  }, [key, currentUser, activeWorkspaceId, localDismissal, serverDismissed, refreshCustomerData]);

  return { dismissed: serverDismissed || localDismissal !== null, dismiss };
}
